import { Check, Navigation } from 'lucide-react';

import { PortalRenderer } from '@/components/PortalRenderer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useConfigStore } from '@/store/ConfigStore';
import { useSearchStore } from '@/store/SearchStore';

export const RouteSteps = () => {
  const portalSelector = useConfigStore((state) => state.portals?.['route-step-by-step']);

  return (
    <PortalRenderer portalSelector={portalSelector}>
      <RouteStepsInternal />
    </PortalRenderer>
  );
};

const RouteStepsInternal = () => {
  const { selectedRoute, activeStep } = useSearchStore();
  const steps = selectedRoute?.legs.flatMap((leg) => leg.steps);

  if (!selectedRoute || !steps?.length) {
    return null;
  }

  return (
    <ScrollArea className="-mr-4 h-[calc(100dvh-25rem)] pr-4 lg:h-[calc(100vh-25rem)] print:h-full" type="auto">
      <div className="flex flex-col [&>*]:border-b-[1px]">
        {steps.map((step, index) => (
          <RouteStep key={index} step={step} active={index === activeStep} done={index < (activeStep ?? 0)} />
        ))}
      </div>
    </ScrollArea>
  );
};

export const ActiveStep = () => {
  const { activeStep, selectedRoute } = useSearchStore();

  if (typeof activeStep !== 'number') {
    return null;
  }

  const step = selectedRoute?.legs.flatMap((leg) => leg.steps)[activeStep];

  if (!step) {
    return null;
  }

  return (
    <div className="bg-secondary dark:bg-secondary/50 px-6 py-1">
      <RouteStep step={step} active />
    </div>
  );
};

const RouteStep = ({ step, active, done }: { step: google.maps.DirectionsStep; active: boolean; done?: boolean }) => (
  <div className="border-b-border print:border-b-accent lt-tiny:py-0 flex items-center gap-4 py-2 print:py-0 [&_b]:text-[var(--blue-one)]">
    {done && <Check className="my-auto h-4 w-4 text-[var(--blue-one)] print:hidden" />}
    {active && <Navigation className="my-auto h-10 w-10 text-[var(--blue-one)] print:hidden" />}
    <div className="text-balance" dangerouslySetInnerHTML={{ __html: step.instructions }} />
    <Maneuver maneuver={step.maneuver} distance={step.distance?.text} />
  </div>
);

const Maneuver = ({ maneuver, distance }: { maneuver: string; distance?: string }) => (
  <div className="ml-auto flex flex-col items-end justify-center gap-1 whitespace-nowrap tabular-nums dark:opacity-75">
    {maneuver.includes('right') ? <ArrowRight /> : maneuver.includes('left') ? <ArrowLeft /> : <ArrowUp />}
    {distance}
  </div>
);

const ArrowUp = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.067 7.19219C13.009 7.2503 12.9401 7.2964 12.8642 7.32785C12.7883 7.35931 12.707 7.3755 12.6249 7.3755C12.5427 7.3755 12.4614 7.35931 12.3855 7.32785C12.3096 7.2964 12.2407 7.2503 12.1827 7.19219L7.62486 2.6336V14.875C7.62486 15.0408 7.55901 15.1997 7.4418 15.3169C7.32459 15.4342 7.16562 15.5 6.99986 15.5C6.8341 15.5 6.67513 15.4342 6.55792 15.3169C6.44071 15.1997 6.37486 15.0408 6.37486 14.875V2.6336L1.81705 7.19219C1.69977 7.30947 1.54071 7.37535 1.37486 7.37535C1.20901 7.37535 1.04995 7.30947 0.932672 7.19219C0.815396 7.07492 0.749512 6.91586 0.749512 6.75C0.749512 6.58415 0.815396 6.42509 0.932672 6.30782L6.55767 0.682816C6.61572 0.624706 6.68465 0.578606 6.76052 0.547154C6.8364 0.515701 6.91772 0.499512 6.99986 0.499512C7.08199 0.499512 7.16332 0.515701 7.2392 0.547154C7.31507 0.578606 7.384 0.624706 7.44205 0.682816L13.067 6.30782C13.1252 6.36586 13.1713 6.43479 13.2027 6.51067C13.2342 6.58654 13.2504 6.66787 13.2504 6.75C13.2504 6.83214 13.2342 6.91347 13.2027 6.98934C13.1713 7.06522 13.1252 7.13415 13.067 7.19219Z" />
  </svg>
);

const ArrowRight = ({ left }: { left?: boolean }) => (
  <svg
    width="18"
    height="14"
    viewBox="0 0 18 14"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={left ? 'scale-x-[-1]' : ''}
  >
    <path d="M16.9422 5.56717L13.1922 9.31717C13.0749 9.43444 12.9159 9.50033 12.75 9.50033C12.5841 9.50033 12.4251 9.43444 12.3078 9.31717C12.1905 9.19989 12.1247 9.04083 12.1247 8.87498C12.1247 8.70913 12.1905 8.55007 12.3078 8.43279L14.9914 5.74998H9C7.17727 5.75205 5.42979 6.47704 4.14092 7.7659C2.85206 9.05477 2.12707 10.8023 2.125 12.625C2.125 12.7907 2.05915 12.9497 1.94194 13.0669C1.82473 13.1841 1.66576 13.25 1.5 13.25C1.33424 13.25 1.17527 13.1841 1.05806 13.0669C0.940848 12.9497 0.875 12.7907 0.875 12.625C0.877275 10.4708 1.73403 8.40549 3.25727 6.88225C4.78051 5.35901 6.84581 4.50226 9 4.49998H14.9914L12.3078 1.81717C12.2497 1.7591 12.2037 1.69016 12.1723 1.61429C12.1408 1.53842 12.1247 1.4571 12.1247 1.37498C12.1247 1.29286 12.1408 1.21154 12.1723 1.13567C12.2037 1.0598 12.2497 0.990863 12.3078 0.932794C12.4251 0.815518 12.5841 0.749634 12.75 0.749634C12.8321 0.749634 12.9134 0.765809 12.9893 0.797236C13.0652 0.828662 13.1341 0.874725 13.1922 0.932794L16.9422 4.68279C17.0003 4.74084 17.0464 4.80977 17.0779 4.88564C17.1093 4.96152 17.1255 5.04285 17.1255 5.12498C17.1255 5.20712 17.1093 5.28844 17.0779 5.36432C17.0464 5.44019 17.0003 5.50912 16.9422 5.56717Z" />
  </svg>
);

const ArrowLeft = () => <ArrowRight left />;
